/**
 * Browser fetch wrapper that stamps `x-request-id` on every outbound request.
 *
 * The id is generated per top-level user action (a button click, a form
 * submit, a page navigation). All sub-requests triggered by that action share
 * the same id so server logs can be filtered to a single user interaction.
 *
 * Usage:
 *   import { fetchWithRequestId, newRequestId } from '@/lib/fetch-with-request-id';
 *
 *   // Per user action — generate a fresh id
 *   const requestId = newRequestId();
 *   await fetchWithRequestId('/api/things', { requestId });
 *
 *   // Or as a one-off (auto-generated id)
 *   await fetchWithRequestId('/api/things');
 *
 * The response's `x-request-id` header is logged to the console so QA can
 * grep server logs by it without DevTools network panel.
 */

export function newRequestId() {
  return crypto.randomUUID();
}

/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit & { requestId?: string }} [init]
 * @returns {Promise<Response>}
 */
export async function fetchWithRequestId(input, init = {}) {
  const { requestId = newRequestId(), headers, ...rest } = init;
  const merged = new Headers(headers);
  merged.set('x-request-id', requestId);

  const response = await fetch(input, { ...rest, headers: merged });

  // Log the id alongside the URL so QA and devs can correlate to server logs.
  // Suppress in production to keep the console clean.
  if (import.meta.env?.DEV) {
    const echoed = response.headers.get('x-request-id') ?? '(none)';
    const url = typeof input === 'string' ? input : input.url ?? String(input);
    console.debug(`[request-id] ${response.status} ${url} → ${echoed}`);
  }

  return response;
}
